import { useCallback } from 'react';

import { apiPatchAsUser } from '../lib/api_v2';
import { queueWorkspaceBackgroundUpdate } from '../lib/backgroundImage';
import db from '../lib/db';
import { logError } from '../lib/logger';
import type { WorkspaceBackgroundPreset } from '../lib/workspaceBackgroundPresets';

interface WorkspaceBackgroundImage {
  id?: string;
  [key: string]: unknown;
}

interface UpdatedWorkspace {
  id?: string;
  background_image?: WorkspaceBackgroundImage | null;
  background_image_preset?: WorkspaceBackgroundPreset | null;
  background_image_url?: string | null;
  [key: string]: unknown;
}

interface WorkspaceUpdateResponse {
  ok?: boolean;
  errors?: unknown;
  validations?: unknown;
  data?: {
    workspace?: UpdatedWorkspace;
  };
}

type BackgroundImageAttributes =
  | { background_image_id: string; background_image_preset?: never }
  | { background_image_source_id: string; background_image_id?: never; background_image_preset?: never }
  | { background_image_preset: WorkspaceBackgroundPreset | ''; background_image_id?: never };

const useWorkspaceBackgroundImageMutation = (workspaceId?: string | null) => {
  const updateBackgroundImage = useCallback(
    async (attributes: BackgroundImageAttributes): Promise<UpdatedWorkspace | null> => {
      if (!workspaceId) return null;

      return queueWorkspaceBackgroundUpdate(workspaceId, async () => {
        const response = await apiPatchAsUser(`/workspaces/${workspaceId}`, { workspace: attributes });
        const data = (await response.json()) as WorkspaceUpdateResponse;

        if (!(response.ok && data.ok === true)) {
          logError('useWorkspaceBackgroundImageMutation', 'Failed to save workspace background image', {
            errors: data.errors || data.validations,
          });
          throw new Error('save_failed');
        }

        const updatedWorkspace = data.data?.workspace;
        if (updatedWorkspace?.id) {
          try {
            await db.table<UpdatedWorkspace, string>('workspaces').put(updatedWorkspace);
          } catch (cacheError) {
            logError('useWorkspaceBackgroundImageMutation', 'Failed to cache updated workspace', { error: cacheError });
          }
        }

        return updatedWorkspace || null;
      });
    },
    [workspaceId],
  );

  const saveAttachment = useCallback(
    async (attachmentId?: string | null): Promise<WorkspaceBackgroundImage | null> => {
      const updatedWorkspace = await updateBackgroundImage({ background_image_id: attachmentId || '' });
      return updatedWorkspace?.background_image || null;
    },
    [updateBackgroundImage],
  );

  const savePreset = useCallback(
    async (preset?: WorkspaceBackgroundPreset | null): Promise<UpdatedWorkspace | null> => {
      return updateBackgroundImage({ background_image_preset: preset || '' });
    },
    [updateBackgroundImage],
  );

  const saveExistingAttachment = useCallback(
    async (attachmentId: string): Promise<WorkspaceBackgroundImage | null> => {
      const updatedWorkspace = await updateBackgroundImage({ background_image_source_id: attachmentId });
      return updatedWorkspace?.background_image || null;
    },
    [updateBackgroundImage],
  );

  return { saveAttachment, saveExistingAttachment, savePreset };
};

export default useWorkspaceBackgroundImageMutation;
